import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  ChevronDown,
  RotateCcw,
  Search,
  Settings2,
} from "@/components/icons";
import { toast } from "sonner";
import { useStore } from "@/store/store";
import { IliaAvatar, IliaMark } from "@/components/askilia/atoms";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useI18n } from "@/i18n";

const ROLE_KEYS = { Owner: "role.owner", Manager: "role.manager", Agent: "role.agent", Operator: "role.agent" };

export function Topbar() {
  const { state, actions } = useStore();
  const { t, localize, formatDate } = useI18n();
  const nav = useNavigate();
  const [query, setQuery] = React.useState("");
  const openConversations = state.conversations.filter(
    (conversation) => !conversation.archivedAt && conversation.lifecycle !== "resolved"
  );
  const handling = openConversations.filter((conversation) => conversation.lifecycle === "ilia").length;
  const waiting = openConversations.filter((conversation) => conversation.waitingOn === "team").length;
  const unread = openConversations.filter((conversation) => conversation.unread).length;
  const firstName = localize(state.user.name).split(" ")[0];
  const today = formatDate(new Date(), {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const setLanguage = (language) => {
    actions.setLang(language);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const value = query.trim();
    if (!value) return;
    const needle = value.toLowerCase();
    const customer = state.customers.find((item) => [
      item.name,
      item.email,
      item.phone,
      item.valueBasis,
    ].some((field) => String(field || "").toLowerCase().includes(needle)));
    if (customer) {
      nav(`/customers/${customer.id}`);
      return;
    }

    const conversation = state.conversations.find((item) => (
      item.messages || []
    ).some((message) => String(message.text || "").toLowerCase().includes(needle)));
    if (conversation) {
      nav(`/inbox/${conversation.id}`);
      return;
    }

    toast(t("topbar.search.noMatch"), {
      description: t("topbar.search.noMatchHint"),
    });
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center border-b border-border bg-surface/95 px-4 backdrop-blur lg:px-6">
      <div className="flex w-full items-center gap-3 lg:grid lg:grid-cols-[220px_minmax(280px,520px)_minmax(340px,1fr)] lg:gap-5">
        <div className="flex min-w-0 items-center">
          <Link to="/today" className="flex items-center gap-2 lg:hidden" aria-label={t("action.openToday")}>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white shadow-xs">
              <IliaMark size={26} />
            </span>
            <span className="text-[15px] font-semibold tracking-tight">Askilia</span>
          </Link>
          <Link
            to="/calendar"
            className="hidden min-w-0 items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:flex"
          >
            <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{today}</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Link>
        </div>

        <form onSubmit={submitSearch} role="search" className="relative hidden w-full items-center lg:flex">
          <label htmlFor="global-search" className="sr-only">{t("topbar.search.label")}</label>
          <input
            id="global-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("topbar.search.placeholder")}
            className="h-10 w-full rounded-lg border border-input bg-background pl-3 pr-10 text-sm outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/25"
          />
          <button
            type="submit"
            aria-label={t("topbar.search.submit")}
            className="absolute right-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Search className="h-4 w-4" />
          </button>
        </form>

        <TooltipProvider delayDuration={200}>
          <div className="ml-auto flex min-w-0 items-center justify-end gap-2 lg:ml-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/inbox?unread=1"
                  aria-label={t("topbar.inbox.unreadCount", { count: unread })}
                  className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Bell className="h-5 w-5" />
                  {unread > 0 ? (
                    <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-accent ring-2 ring-surface" />
                  ) : null}
                </Link>
              </TooltipTrigger>
              <TooltipContent>{unread ? t("topbar.inbox.unreadCount", { count: unread }) : t("topbar.inbox.noUnread")}</TooltipContent>
            </Tooltip>

            <Link
              to="/inbox"
              aria-label={t("topbar.inbox.openSummary", { handling, waiting })}
              className="hidden min-w-0 items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-1.5 shadow-xs transition-[border-color,box-shadow] hover:border-primary/35 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex"
            >
              <IliaAvatar size={24} active surface={false} />
              <div className="min-w-0 leading-tight">
                <p className="truncate text-xs font-semibold text-foreground">{t("topbar.inbox.iliaHandlingCount", { count: handling })}</p>
                <p className="hidden text-[11px] text-muted-foreground xl:block">
                  {waiting ? t("topbar.inbox.operatorNeededCount", { count: waiting }) : t("topbar.inbox.underControl")}
                </p>
              </div>
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t("topbar.account.openMenu", { name: localize(state.user.name) })}
                  className="inline-flex h-10 items-center gap-2 rounded-lg px-1.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
                    {state.user.initials}
                  </span>
                  <span className="hidden max-w-24 truncate xl:block">{firstName}</span>
                  <ChevronDown className="hidden h-4 w-4 text-muted-foreground xl:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="leading-tight">
                    <p className="text-sm font-medium">{localize(state.user.name)}</p>
                    <p className="text-xs text-muted-foreground">{t(ROLE_KEYS[state.user.role] || "role.agent")} · {localize(state.clinic.workspace)}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings"><Settings2 className="mr-2 h-4 w-4" /> {t("action.settings")}</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="pb-1 text-xs font-medium text-muted-foreground">{t("language.title")}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setLanguage("en")}>
                  <span className="mr-2 inline-flex w-5 justify-center text-xs font-semibold">EN</span>
                  {t("language.english")} {state.lang === "en" ? <span className="ml-auto text-primary">{t("language.active")}</span> : null}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage("ka")}>
                  <span className="mr-2 inline-flex w-5 justify-center text-xs font-semibold">KA</span>
                  {t("language.georgian")} {state.lang === "ka" ? <span className="ml-auto text-primary">{t("language.active")}</span> : null}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={actions.reset}>
                  <RotateCcw className="mr-2 h-4 w-4" /> {t("action.resetPrototype")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TooltipProvider>
      </div>
    </header>
  );
}
